import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  TextField,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { INSURANCES } from '../../config/insurances';
import './FacilitySettingsDialog.css';

const defaultOptions = [
  { label: 'family', baseCost: 0, perMile: 0 },
  { label: 'facility', baseCost: 0, perMile: 0 },
  { label: 'local', baseCost: 0, perMile: 0 },
  { label: 'other', baseCost: 0, perMile: 0 },
];

const parseLocation = (loc) => {
  if (typeof loc === 'string') {
    return { address: loc, lat: null, lng: null };
  } else if (loc) {
    return {
      ...loc,
      lat: loc.lat != null ? Number(loc.lat) : null,
      lng: loc.lng != null ? Number(loc.lng) : null,
    };
  }
  return { address: '', lat: null, lng: null };
};

const buildOptions = (data) => ({
  wheelchair: INSURANCES.reduce((acc, insurance) => {
    acc[insurance] = data?.wheelchair?.options?.[insurance] || defaultOptions;
    return acc;
  }, {}),
  stretcher: INSURANCES.reduce((acc, insurance) => {
    acc[insurance] = data?.stretcher?.options?.[insurance] || defaultOptions;
    return acc;
  }, {}),
});

function FacilitySettingsDialog({
  open,
  onClose,
  facilitySettings,
  facilityId,
}) {
  const [adminEmails, setAdminEmails] = useState([]);
  const [facilityLocation, setFacilityLocation] = useState({
    address: '',
    lat: null,
    lng: null,
  });
  const [locationInput, setLocationInput] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);

  const [transportationType, setTransportationType] = useState('wheelchair');
  const [options, setOptions] = useState({
    wheelchair: INSURANCES.reduce((acc, insurance) => {
      acc[insurance] = defaultOptions;
      return acc;
    }, {}),
    stretcher: INSURANCES.reduce((acc, insurance) => {
      acc[insurance] = defaultOptions;
      return acc;
    }, {}),
  });

  useEffect(() => {
    if (open && facilityId) {
      const fetchData = async () => {
        try {
          const facilitySetupRef = doc(db, 'facilitySetup', facilityId);
          const docSnap = await getDoc(facilitySetupRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setFacilityLocation(parseLocation(data.location));
            setOptions(buildOptions(data));
            setAdminEmails(data.adminEmails || []);
          } else {
            setFacilityLocation({ address: '', lat: null, lng: null });
            setOptions(buildOptions());
            setAdminEmails([]);
          }
        } catch (error) {
          console.error('Error fetching facility settings:', error);
        }
      };
      fetchData();
    }
  }, [open, facilityId]);

  useEffect(() => {
    if (facilitySettings && facilitySettings.location) {
      setFacilityLocation(parseLocation(facilitySettings.location));
      setOptions(buildOptions(facilitySettings));
      setAdminEmails(facilitySettings.adminEmails || []);
    } else {
      setFacilityLocation({ address: '', lat: null, lng: null });
      setOptions(buildOptions());
      setAdminEmails([]);
    }
  }, [facilitySettings]);

  useEffect(() => {
    if (open) {
      setLocationInput(facilityLocation.address);
    }
  }, [open, facilityLocation.address]);

  useEffect(() => {
    if (
      locationInput &&
      locationInput.length > 3 &&
      window.google &&
      window.google.maps &&
      window.google.maps.places &&
      window.google.maps.places.AutocompleteSuggestion
    ) {
      (async () => {
        try {
          const sessionToken =
            new window.google.maps.places.AutocompleteSessionToken();
          const request = { input: locationInput, sessionToken };
          const { suggestions } =
            await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              request,
            );
          setLocationSuggestions(suggestions);
        } catch (error) {
          console.error('Error fetching location suggestions:', error);
        }
      })();
    } else {
      setLocationSuggestions([]);
    }
  }, [locationInput]);

  const safeParse = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const handleOptionChange = useCallback(
    (insurance, index, field, value) => {
      setOptions((prev) => ({
        ...prev,
        [transportationType]: {
          ...prev[transportationType],
          [insurance]: prev[transportationType][insurance].map((opt, i) =>
            i === index
              ? {
                  ...opt,
                  [field]: field === 'label' ? value : safeParse(value),
                }
              : opt,
          ),
        },
      }));
    },
    [transportationType],
  );

  const handleAddOption = useCallback(
    (insurance) => {
      setOptions((prev) => ({
        ...prev,
        [transportationType]: {
          ...prev[transportationType],
          [insurance]: [
            ...prev[transportationType][insurance],
            { label: '', baseCost: 0, perMile: 0 },
          ],
        },
      }));
    },
    [transportationType],
  );

  const handleRemoveOption = useCallback(
    (insurance, index) => {
      setOptions((prev) => ({
        ...prev,
        [transportationType]: {
          ...prev[transportationType],
          [insurance]: prev[transportationType][insurance].filter(
            (_, i) => i !== index,
          ),
        },
      }));
    },
    [transportationType],
  );

  const handleSave = async () => {
    try {
      if (!facilityId) throw new Error('facilityId is undefined');
      const facilitySetupRef = doc(db, 'facilitySetup', facilityId);
      await setDoc(
        facilitySetupRef,
        {
          location: facilityLocation,
          wheelchair: { options: options.wheelchair },
          stretcher: { options: options.stretcher },
          adminEmails: adminEmails,
        },
        { merge: true },
      );
      onClose();
    } catch (error) {
      console.error('Error saving facility settings:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Manage Facility Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ my: 2 }}>
          <Autocomplete
            freeSolo
            options={locationSuggestions}
            getOptionLabel={(option) =>
              typeof option === 'string'
                ? option
                : option.placePrediction.text.text
            }
            inputValue={locationInput}
            onInputChange={(event, newInputValue) =>
              setLocationInput(newInputValue)
            }
            onChange={async (event, selectedOption) => {
              if (selectedOption && typeof selectedOption !== 'string') {
                const placePrediction = selectedOption.placePrediction;
                const place = placePrediction.toPlace();
                await place.fetchFields({
                  fields: ['formattedAddress', 'location'],
                });
                const address = place.formattedAddress;
                const lat = place.location.lat();
                const lng = place.location.lng();
                setFacilityLocation({ address, lat, lng });
                setLocationInput(address);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Facility Location"
                placeholder="Enter an address"
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={adminEmails}
            onChange={(event, newValue) => setAdminEmails(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Facility Admin Email(s)"
                placeholder="Enter email(s)"
              />
            )}
          />
        </Box>

        <Box className="FacilitySettingsSelectionContainer">
          <ToggleButtonGroup
            value={transportationType}
            exclusive
            onChange={(e, val) => {
              if (val) setTransportationType(val);
            }}
          >
            <ToggleButton value="wheelchair">Wheelchair</ToggleButton>
            <ToggleButton value="stretcher">Stretcher</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box className="FacilitySettingsInsurancesContainer">
          {INSURANCES.map((insurance) => (
            <Box className="SettingsInsurance" key={insurance}>
              <p className="SettingsInsuranceTitle">{insurance}</p>
              <div className="SettingsInsuranceHeaders">
                <p>Label</p>
                <p>Base Cost</p>
                <p>Per Mile</p>
              </div>
              {options[transportationType][insurance].map((opt, index) => (
                <Box
                  className="SettingsInsuranceOption"
                  key={index}
                  sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                >
                  <TextField
                    value={opt.label}
                    onChange={(e) =>
                      handleOptionChange(
                        insurance,
                        index,
                        'label',
                        e.target.value,
                      )
                    }
                    sx={{ mr: 1, flex: 1 }}
                  />
                  <TextField
                    value={opt.baseCost}
                    onChange={(e) =>
                      handleOptionChange(
                        insurance,
                        index,
                        'baseCost',
                        e.target.value,
                      )
                    }
                    sx={{ mr: 1, width: '100px' }}
                  />
                  <TextField
                    value={opt.perMile}
                    onChange={(e) =>
                      handleOptionChange(
                        insurance,
                        index,
                        'perMile',
                        e.target.value,
                      )
                    }
                    sx={{ mr: 1, width: '100px' }}
                  />
                  <IconButton
                    onClick={() => handleRemoveOption(insurance, index)}
                  >
                    <Remove />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                className="AddOptionButton"
                startIcon={<Add />}
                onClick={() => handleAddOption(insurance)}
              >
                Add Option
              </Button>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FacilitySettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  facilitySettings: PropTypes.shape({
    location: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        address: PropTypes.string,
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
    ]),
    wheelchair: PropTypes.shape({
      options: PropTypes.object,
    }),
    stretcher: PropTypes.shape({
      options: PropTypes.object,
    }),
    adminEmails: PropTypes.array,
  }),
  facilityId: PropTypes.string.isRequired,
};

export default FacilitySettingsDialog;
